import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { adminApiMessage, evalsApi } from '../adminApi';
import { agentLabel, agentVersions } from '../evals/agents';
import { EvalBadge, EvalError, GateBadge, RunLink } from '../evals/EvalBits';
import {
  caseDiffCounts,
  deltaRows,
  missingText,
  notComparableText,
  outcomeMarks,
} from '../evals/compareView';
import { gradingLabel, shortSha, surfaceLabel } from '../evals/format';
import { compareHref, runHref } from '../evals/query';
import type {
  EvalCaseDiff,
  EvalCompareRunHeader,
  EvalComparison,
  EvalModelComparison,
  EvalRole,
} from '../evals/types';
import { GuideLink } from '../evals/GuideLink';
import { formatDate } from '../format';

// Compare two stored eval runs model by model (task eval_admin_runs), or a run
// with each model's pinned baseline when no second run is named. Run A is the
// run under review and run B the reference: a case is "newly failing" when it
// passed in B and fails in A, and each metric's difference is A − B. A
// difference the comparison cannot resolve is marked "not distinguishable"
// (ADR 0235), never read as better or worse.

function RunHeader({
  label,
  run,
  roles,
}: {
  label: string;
  run: EvalCompareRunHeader;
  /** The agents the comparison reads (TOM-533): each named with its own version. */
  roles: ReadonlyArray<{ role: EvalRole }>;
}) {
  const facts = [
    formatDate(run.startedAt ?? run.createdAt),
    `${run.suite}${run.split !== null ? ` (${run.split})` : ''}`,
    `${run.repeats}×`,
    run.fullSelection ? null : 'part of the suite',
    run.gitSha !== null ? `git ${shortSha(run.gitSha)}` : 'no git sha recorded',
    ...agentVersions(run, roles).map((a) =>
      a.version !== null ? `${a.label} ${a.version}` : null
    ),
  ].filter((x): x is string => x !== null);
  return (
    <div className="eval-compare-run">
      <span className="eval-compare-label">{label}</span>
      <RunLink id={run.id} />
      <span className="admin-muted eval-sub" data-testid="eval-compare-run-facts">
        {facts.join(' · ')}
      </span>
    </div>
  );
}

function CaseList({
  title,
  cases,
  testId,
  tone,
}: {
  title: string;
  cases: EvalCaseDiff[];
  testId: string;
  tone: 'bad' | 'good' | 'warn' | 'muted';
}) {
  if (cases.length === 0) return null;
  return (
    <div className={`eval-diff-list eval-diff-list--${tone}`} data-testid={testId}>
      <h4>
        {title} ({cases.length})
      </h4>
      <div className="scroll-x">
        <table className="admin-table eval-table--compact">
          <thead>
            <tr>
              <th>Case</th>
              <th>Kind</th>
              <th>A</th>
              <th>B</th>
            </tr>
          </thead>
          <tbody>
            {cases.map((c) => (
              <tr key={c.caseId}>
                <td>
                  <code>{c.caseId}</code>
                </td>
                <td>{surfaceLabel(c.surface)}</td>
                {(['a', 'b'] as const).map((s) => (
                  <td key={s}>
                    <span className="eval-marks">{outcomeMarks(c[s].outcomes)}</span>
                    {c[s].flaky ? <span className="admin-muted"> flaky</span> : null}
                    {c.decision !== null ? (
                      <span
                        className="admin-muted eval-sub eval-diff-decision"
                        data-testid="eval-diff-decision"
                      >
                        decision{' '}
                        <span className="eval-marks">{outcomeMarks(c.decision[s].outcomes)}</span>
                        {c.decision[s].flaky ? ' flaky' : ''}
                      </span>
                    ) : null}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ModelComparison({ m }: { m: EvalModelComparison }) {
  const missing = missingText(m);
  return (
    <article className="eval-card" data-testid={`eval-compare-${m.role}-${m.model}`}>
      <header className="eval-card-header">
        <div>
          <h3>
            <code>{m.model}</code>
            <span className="admin-muted eval-nowrap"> · {agentLabel(m.role)}</span>
          </h3>
          <p className="admin-muted eval-sub">
            A: {gradingLabel(m.a.grading)}
            {m.b !== null ? ` · B: ${gradingLabel(m.b.grading)}` : ''}
            {m.b !== null ? (
              <>
                {' '}
                · B is <Link to={runHref(m.b.runId)}>{m.b.runId.slice(0, 8)}</Link>
              </>
            ) : null}
          </p>
        </div>
        <span className="eval-gates">
          <span className="eval-nowrap">
            A <GateBadge gate={m.a.gate} reason={m.a.gateReason} />
          </span>
          {m.b !== null ? (
            <span className="eval-nowrap">
              B <GateBadge gate={m.b.gate} reason={m.b.gateReason} />
            </span>
          ) : null}
        </span>
      </header>

      {missing !== null || m.diff === null ? (
        <p className="admin-empty" data-testid="eval-compare-missing">
          {missing ?? 'Nothing to compare.'}
        </p>
      ) : (
        <>
          <div className="scroll-x">
            <table
              className="admin-table eval-table--compact eval-compare-table"
              data-testid="eval-compare-metrics"
            >
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Split</th>
                  <th>A</th>
                  <th>B</th>
                  <th>A − B</th>
                  <th>95% interval</th>
                  <th>Reads</th>
                </tr>
              </thead>
              <tbody>
                {deltaRows(m.diff).map((d) => (
                  <tr key={d.key}>
                    <td>{d.metric}</td>
                    <td>{d.split}</td>
                    <td>{d.a}</td>
                    <td>{d.b}</td>
                    <td>{d.difference}</td>
                    <td className="admin-muted">{d.interval}</td>
                    <td>
                      {d.verdict === 'not_distinguishable' ? (
                        <EvalBadge
                          tone="muted"
                          title="Inside what this comparison can resolve: the interval holds zero, it is one case or less, or it is no wider than the noise."
                          testId="eval-not-distinguishable"
                        >
                          {d.verdictText}
                        </EvalBadge>
                      ) : d.verdict === 'higher' ? (
                        <EvalBadge tone="succeeded">{d.verdictText}</EvalBadge>
                      ) : d.verdict === 'lower' ? (
                        <EvalBadge tone="failed">{d.verdictText}</EvalBadge>
                      ) : (
                        <span className="admin-muted">{d.verdictText}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <CaseSummary m={m} />
        </>
      )}
    </article>
  );
}

function CaseSummary({ m }: { m: EvalModelComparison }) {
  if (m.diff === null) return null;
  const counts = caseDiffCounts(m.diff);
  const c = m.diff.cases;
  return (
    <>
      <p className="eval-diff-counts" data-testid="eval-compare-counts">
        <span className={counts.newlyFailing > 0 ? 'eval-error-text' : undefined}>
          {counts.newlyFailing} newly failing
        </span>
        {' · '}
        <span className={counts.newlyPassing > 0 ? 'eval-ok-text' : undefined}>
          {counts.newlyPassing} newly passing
        </span>
        {' · '}
        {counts.stillFailing} still failing · {counts.flaky} flaky in either run ·{' '}
        {counts.unchanged} unchanged
        {counts.notComparable > 0 ? ` · ${counts.notComparable} not comparable` : ''}
        {counts.onlyOne > 0 ? ` · ${counts.onlyOne} in one run only` : ''}
      </p>
      <CaseList
        title="Newly failing"
        cases={c.newlyFailing}
        testId="eval-newly-failing"
        tone="bad"
      />
      <CaseList
        title="Newly passing"
        cases={c.newlyPassing}
        testId="eval-newly-passing"
        tone="good"
      />
      <CaseList
        title="Still failing"
        cases={c.stillFailing}
        testId="eval-still-failing"
        tone="warn"
      />
      <CaseList title="Flaky in either run" cases={c.flaky} testId="eval-flaky" tone="warn" />
      {c.notComparable.length > 0 ? (
        <details className="eval-drawer-details" data-testid="eval-not-comparable">
          <summary>Not comparable ({c.notComparable.length})</summary>
          <ul className="eval-list">
            {c.notComparable.map((n) => (
              <li key={n.caseId}>
                <code>{n.caseId}</code>: {notComparableText(n.reason)}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {c.onlyA.length + c.onlyB.length > 0 ? (
        <details className="eval-drawer-details">
          <summary>In one run only ({c.onlyA.length + c.onlyB.length})</summary>
          {c.onlyA.length > 0 ? (
            <p>
              Only A:{' '}
              {c.onlyA.map((id) => (
                <code key={id}>{id} </code>
              ))}
            </p>
          ) : null}
          {c.onlyB.length > 0 ? (
            <p>
              Only B:{' '}
              {c.onlyB.map((id) => (
                <code key={id}>{id} </code>
              ))}
            </p>
          ) : null}
        </details>
      ) : null}
    </>
  );
}

export function AdminEvalCompare() {
  const [search] = useSearchParams();
  const a = search.get('a') ?? '';
  const b = search.get('b');
  const [data, setData] = useState<EvalComparison | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    setData(null);
    setError(null);
    if (a.length === 0) {
      setError('Choose a run to compare: open a run and pick one to compare it with.');
      return;
    }
    evalsApi
      .compare(a, b)
      .then((res) => {
        if (!ignore) setData(res);
      })
      .catch((err) => {
        if (ignore) return;
        setError(
          (err as { status?: number }).status === 404
            ? 'One of these runs is not stored.'
            : adminApiMessage(err, 'Failed to compare these runs.')
        );
      });
    return () => {
      ignore = true;
    };
  }, [a, b]);

  return (
    <section className="admin-section eval-page">
      <p className="admin-hint">
        <Link to="/admin/evals/runs">← All runs</Link>
      </p>
      <header className="admin-section-header">
        <h1>Compare runs</h1>
        {data !== null && data.mode === 'runs' && data.b !== null ? (
          <span className="admin-count">
            <Link to={compareHref(data.b.id, data.a.id)}>Swap A and B</Link>
          </span>
        ) : null}
        <GuideLink section="compare" />
      </header>
      <p className="admin-hint">
        A is the run under review, B the reference. A case is newly failing when it passed in B and
        fails in A. Differences are A − B with their 95% interval; one inside the noise reads
        &quot;not distinguishable&quot;.
      </p>

      <EvalError message={error} />
      {data === null && error === null ? <p className="admin-loading-row">Loading…</p> : null}

      {data !== null ? (
        <>
          <div className="eval-compare-runs" data-testid="eval-compare-runs">
            <RunHeader label="A" run={data.a} roles={data.models} />
            {data.mode === 'runs' && data.b !== null ? (
              <RunHeader label="B" run={data.b} roles={[...data.models, ...data.onlyInB]} />
            ) : (
              <div className="eval-compare-run">
                <span className="eval-compare-label">B</span>
                <span>Each model&apos;s pinned baseline</span>
                {data.baselineRuns.length > 0 ? (
                  <span className="admin-muted eval-sub">
                    {data.baselineRuns.map((r) => (
                      <RunLink key={r.id} id={r.id} />
                    ))}
                  </span>
                ) : (
                  <span className="admin-muted eval-sub">None pinned for this suite yet.</span>
                )}
              </div>
            )}
          </div>

          <div className="eval-cards eval-cards--stack">
            {data.models.map((m) => (
              <ModelComparison key={`${m.role}|${m.model}`} m={m} />
            ))}
          </div>

          {data.onlyInB.length > 0 ? (
            <p className="admin-hint">
              Only run B ran:{' '}
              {data.onlyInB.map((m) => (
                <code key={`${m.role}|${m.model}`}>{m.model} </code>
              ))}
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
